import { Bookmark, Heart, MessageCircle, MoreHorizontal, Send } from "lucide-react";
import { useRef, useState } from "react";
import type { FeedPost } from "../../shared/types";
import { api } from "../lib/api";
import { Verified } from "./Verified";

export function PostCard({ post, onChange }: { post: FeedPost; onChange?: (post: FeedPost) => void }) {
  const [local, setLocal] = useState(post);
  const [comment, setComment] = useState("");
  const commentInput = useRef<HTMLInputElement>(null);
  const commentForm = useRef<HTMLFormElement>(null);

  async function optimistic(kind: "like" | "save") {
    const snapshot = local;
    const next = {
      ...local,
      viewerHasLiked: kind === "like" ? !local.viewerHasLiked : local.viewerHasLiked,
      viewerHasSaved: kind === "save" ? !local.viewerHasSaved : local.viewerHasSaved,
      likeCount: kind === "like" ? local.likeCount + (local.viewerHasLiked ? -1 : 1) : local.likeCount,
      saveCount: kind === "save" ? local.saveCount + (local.viewerHasSaved ? -1 : 1) : local.saveCount
    };
    setLocal(next);
    try {
      const updated = kind === "like" ? await api.like(local.id, snapshot.viewerHasLiked) : await api.save(local.id, snapshot.viewerHasSaved);
      if (updated) {
        setLocal(updated);
        onChange?.(updated);
      }
    } catch {
      setLocal(snapshot);
    }
  }

  function openCommentBox() {
    commentForm.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => commentInput.current?.focus(), 120);
  }

  async function submitComment(event: React.FormEvent) {
    event.preventDefault();
    const text = comment.trim();
    if (!text) return;
    await api.comment(local.id, text);
    setLocal({
      ...local,
      commentCount: local.commentCount + 1,
      commentPreview: [
        ...local.commentPreview,
        { id: `local-${Date.now()}`, body: text, author: { username: "you", displayName: "You", avatarUrl: "", verified: false } }
      ].slice(-2)
    });
    setComment("");
  }

  const media = local.media[0];
  return (
    <article className="post">
      <header className="post-head">
        <a href={`/@${local.author.username}`} className="avatar-link"><img src={local.author.avatarUrl} alt="" /></a>
        <div>
          <a href={`/@${local.author.username}`} className="name">{local.author.displayName}<Verified show={local.author.citizenVerified || local.author.verified} /></a>
          <span>@{local.author.username}{local.location ? ` · ${local.location}` : ""}</span>
        </div>
        <button className="icon-button" aria-label="More options"><MoreHorizontal size={20} /></button>
      </header>
      {media ? (
        <a href={`/p/${local.id}`} className={`media-box ratio-${local.aspectRatio.replace(":", "-")} ${local.postType === "video" ? "flip-preview" : ""}`}>
          <img src={media.url} alt={media.altText} loading="lazy" />
          {local.media.length > 1 && <span className="carousel-badge">1/{local.media.length}</span>}
        </a>
      ) : (
        <a href={`/p/${local.id}`} className="text-post">{local.caption}</a>
      )}
      <div className="actions">
        <button className={local.viewerHasLiked ? "active" : ""} onClick={() => void optimistic("like")} aria-label={local.viewerHasLiked ? "Unlike" : "Like"}><Heart size={23} /></button>
        <button type="button" onClick={openCommentBox} aria-label="Write a comment"><MessageCircle size={23} /></button>
        <button aria-label="Share"><Send size={23} /></button>
        <button className={local.viewerHasSaved ? "active push" : "push"} onClick={() => void optimistic("save")} aria-label={local.viewerHasSaved ? "Unsave" : "Save"}><Bookmark size={23} /></button>
      </div>
      <div className="post-copy">
        <strong>{local.likeCount.toLocaleString()} likes</strong>
        <p><a href={`/@${local.author.username}`}>{local.author.username}</a> {local.caption}</p>
        {!!local.hashtags.length && <p className="tags">{local.hashtags.map((tag) => <a key={tag} href={`/explore?tag=${tag}`}>#{tag}</a>)}</p>}
        {local.commentPreview.map((item) => <p className="muted" key={item.id}><strong>{item.author.username}</strong> {item.body}</p>)}
      </div>
      {local.commentsEnabled && (
        <form className="comment-form" ref={commentForm} onSubmit={(event) => void submitComment(event)}>
          <input ref={commentInput} value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Add a comment..." maxLength={500} />
          <button type="submit">Post</button>
        </form>
      )}
    </article>
  );
}
